/**
 * Festival Header Widget - Enhanced Premium Animations
 * Modern, professional festival animations for dashboard header
 * 13 complete festival themes with cultural authenticity
 */

import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
// @mui
import Tooltip from '@mui/material/Tooltip';
// utils
import axiosInstance, { endpoints } from 'src/utils/axios';

// ----------------------------------------------------------------------

export default function FestivalHeaderWidget() {
    const canvasRef = useRef(null);
    const animationFrameRef = useRef(null);
    const [activeTheme, setActiveTheme] = useState(null);
    const [festivalName, setFestivalName] = useState('');

    // Fetch active festival from API
    useEffect(() => {
        const fetchActiveFestival = async () => {
            try {
                const response = await axiosInstance.post(endpoints.festival.active);
                if (response.data.success && response.data.data.themeKey && response.data.data.themeKey !== 'default') {
                    setActiveTheme(response.data.data.themeKey);
                    setFestivalName(response.data.data.festivalName || 'Festival');
                } else {
                    setActiveTheme(null);
                }
            } catch (error) {
                console.error('Failed to fetch active festival for header:', error);
            }
        };

        fetchActiveFestival();

        // Listen for updates from other components
        const handleUpdate = () => fetchActiveFestival();
        window.addEventListener('festival-update', handleUpdate);

        return () => {
            window.removeEventListener('festival-update', handleUpdate);
        };
    }, []);

    // Premium animation rendering
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || !activeTheme) return undefined;

        const ctx = canvas.getContext('2d');
        const dpr = window.devicePixelRatio || 1;

        // Set canvas size with retina support
        canvas.width = 60 * dpr;
        canvas.height = 40 * dpr;
        canvas.style.width = '60px';
        canvas.style.height = '40px';
        ctx.scale(dpr, dpr);

        let time = 0;
        const centerX = 30;
        const centerY = 20;

        const animate = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.save();

            // Reset shadows
            ctx.shadowBlur = 0;
            ctx.shadowColor = 'transparent';

            const theme = activeTheme?.toLowerCase().replace(/\s+/g, '');

            switch (theme) {
                case 'diwali': {
                    // Enhanced Diya with Multi-layered Fireworks
                    ctx.translate(centerX, centerY + 5);

                    // Diya base with gradient
                    const diyaGradient = ctx.createRadialGradient(0, 0, 0, 0, 0, 8);
                    diyaGradient.addColorStop(0, '#8D6E63');
                    diyaGradient.addColorStop(1, '#5D4037');
                    ctx.fillStyle = diyaGradient;
                    ctx.beginPath();
                    ctx.arc(0, 0, 8, 0, Math.PI, false);
                    ctx.fill();

                    // Oil rim
                    ctx.fillStyle = '#FFD700';
                    ctx.beginPath();
                    ctx.arc(0, 0, 6, 0, Math.PI, false);
                    ctx.lineWidth = 1;
                    ctx.strokeStyle = '#FFA000';
                    ctx.stroke();

                    // Animated flame with realistic flicker
                    const flicker = Math.random() * 0.25 + 0.85;
                    const flameHeight = 8 * flicker;

                    // Flame glow
                    const flameGlow = ctx.createRadialGradient(0, -6, 0, 0, -6, 12);
                    flameGlow.addColorStop(0, 'rgba(255, 200, 50, 0.8)');
                    flameGlow.addColorStop(0.5, 'rgba(255, 100, 50, 0.4)');
                    flameGlow.addColorStop(1, 'rgba(255, 50, 0, 0)');
                    ctx.fillStyle = flameGlow;
                    ctx.fillRect(-12, -18, 24, 24);

                    // Main flame
                    ctx.fillStyle = '#FF6F00';
                    ctx.beginPath();
                    ctx.ellipse(0, -4, 3 * flicker, flameHeight, 0, 0, Math.PI * 2);
                    ctx.fill();

                    ctx.fillStyle = '#FF9800';
                    ctx.beginPath();
                    ctx.ellipse(0, -6, 2.5 * flicker, flameHeight * 0.8, 0, 0, Math.PI * 2);
                    ctx.fill();

                    ctx.fillStyle = '#FFC107';
                    ctx.shadowBlur = 15;
                    ctx.shadowColor = '#FFD700';
                    ctx.beginPath();
                    ctx.ellipse(0, -8, 2 * flicker, flameHeight * 0.6, 0, 0, Math.PI * 2);
                    ctx.fill();
                    ctx.shadowBlur = 0;

                    // Premium fireworks
                    const fireworkColors = ['#FF1744', '#FFD600', '#00E676', '#2979FF', '#E040FB'];

                    for (let fw = 0; fw < 4; fw += 1) {
                        const fwTime = (time + fw * 30) % 120;
                        const fwProgress = fwTime / 120;

                        if (fwProgress < 0.6) {
                            const fwX = -18 + (fw * 12);
                            const fwY = -20 - (fwProgress * 15);
                            const fwRadius = fwProgress * 16;
                            const fwAlpha = (1 - (fwProgress * 1.67)) * 0.9;

                            ctx.globalAlpha = fwAlpha;

                            // Burst particles
                            const particleCount = 12;
                            for (let p = 0; p < particleCount; p += 1) {
                                const angle = (Math.PI * 2 * p) / particleCount;
                                const px = fwX + Math.cos(angle) * fwRadius;
                                const py = fwY + Math.sin(angle) * fwRadius;

                                const color = fireworkColors[fw % fireworkColors.length];

                                // Particle trail
                                ctx.strokeStyle = color;
                                ctx.lineWidth = 1.5;
                                ctx.beginPath();
                                ctx.moveTo(fwX, fwY);
                                ctx.lineTo(px, py);
                                ctx.stroke();

                                // Particle glow
                                ctx.fillStyle = color;
                                ctx.shadowColor = color;
                                ctx.shadowBlur = 10;
                                ctx.beginPath();
                                ctx.arc(px, py, 2.5, 0, Math.PI * 2);
                                ctx.fill();
                            }

                            ctx.globalAlpha = 1;
                            ctx.shadowBlur = 0;
                        }
                    }

                    // Orbiting sparkles
                    for (let s = 0; s < 6; s += 1) {
                        const sparkleAngle = (time * 0.06) + (s * Math.PI * 2 / 6);
                        const sparkleRadius = 14 + Math.sin(time * 0.12 + s) * 3;
                        const sparkleX = Math.cos(sparkleAngle) * sparkleRadius;
                        const sparkleY = Math.sin(sparkleAngle) * sparkleRadius;
                        const sparkleSize = 1.5 + Math.sin(time * 0.18 + s) * 0.7;

                        ctx.fillStyle = '#FFD700';
                        ctx.shadowColor = '#FFD700';
                        ctx.shadowBlur = 8;
                        ctx.beginPath();
                        ctx.arc(sparkleX, sparkleY, sparkleSize, 0, Math.PI * 2);
                        ctx.fill();
                        ctx.shadowBlur = 0;
                    }

                    break;
                }

                case 'holi': {
                    // Enhanced Color Powder Explosion
                    const colors = [
                        { primary: '#E91E63', secondary: '#F8BBD0' },
                        { primary: '#00BCD4', secondary: '#B2EBF2' },
                        { primary: '#FFEB3B', secondary: '#FFF9C4' },
                        { primary: '#9C27B0', secondary: '#E1BEE7' }
                    ];

                    ctx.translate(centerX, centerY);

                    // Multiple powder clouds
                    for (let i = 0; i < 4; i += 1) {
                        const offset = (time * 2.5 + i * 15) % 80;
                        const progress = offset / 80;
                        const alpha = Math.sin(progress * Math.PI) * 0.8;

                        const color = colors[i];
                        const angle = i * Math.PI / 2;
                        const distance = progress * 15;
                        const x = Math.cos(angle + time * 0.05) * distance;
                        const y = Math.sin(angle + time * 0.05) * distance;

                        // Outer cloud
                        const outerGradient = ctx.createRadialGradient(x, y, 0, x, y, 8);
                        outerGradient.addColorStop(0, color.primary + Math.floor(alpha * 255).toString(16).padStart(2, '0'));
                        outerGradient.addColorStop(1, `${color.secondary}00`);
                        ctx.fillStyle = outerGradient;
                        ctx.beginPath();
                        ctx.arc(x, y, 8, 0, Math.PI * 2);
                        ctx.fill();

                        // Inner concentrated color
                        ctx.fillStyle = color.primary;
                        ctx.globalAlpha = alpha;
                        ctx.beginPath();
                        ctx.arc(x, y, 4, 0, Math.PI * 2);
                        ctx.fill();
                        ctx.globalAlpha = 1;

                        // Powder particles
                        for (let p = 0; p < 5; p += 1) {
                            const particleAngle = (p / 5) * Math.PI * 2;
                            const particleRadius = progress * 12;
                            const px = x + Math.cos(particleAngle) * particleRadius;
                            const py = y + Math.sin(particleAngle) * particleRadius;

                            ctx.fillStyle = color.primary;
                            ctx.globalAlpha = alpha * 0.6;
                            ctx.beginPath();
                            ctx.arc(px, py, 2, 0, Math.PI * 2);
                            ctx.fill();
                        }
                        ctx.globalAlpha = 1;
                    }

                    break;
                }

                case 'navratri': {
                    // Premium Dandiya Sticks with Motion Blur
                    ctx.translate(centerX, centerY);
                    const rotationAngle = time * 0.08;

                    // Decorative base circle
                    const baseGradient = ctx.createRadialGradient(0, 0, 0, 0, 0, 10);
                    baseGradient.addColorStop(0, 'rgba(255, 215, 0, 0.3)');
                    baseGradient.addColorStop(1, 'rgba(255, 215, 0, 0)');
                    ctx.fillStyle = baseGradient;
                    ctx.beginPath();
                    ctx.arc(0, 0, 10, 0, Math.PI * 2);
                    ctx.fill();

                    // First stick with gradient
                    ctx.save();
                    ctx.rotate(rotationAngle);

                    const stick1Gradient = ctx.createLinearGradient(-2, -14, 2, 14);
                    stick1Gradient.addColorStop(0, '#AD1457');
                    stick1Gradient.addColorStop(0.5, '#E91E63');
                    stick1Gradient.addColorStop(1, '#AD1457');
                    ctx.fillStyle = stick1Gradient;
                    ctx.shadowColor = '#FF4081';
                    ctx.shadowBlur = 12;
                    ctx.fillRect(-2.5, -14, 5, 28);

                    // Gold bands
                    ctx.fillStyle = '#FFD700';
                    ctx.shadowBlur = 6;
                    ctx.fillRect(-2.5, -10, 5, 3);
                    ctx.fillRect(-2.5, 7, 5, 3);
                    ctx.shadowBlur = 0;
                    ctx.restore();

                    // Second stick
                    ctx.save();
                    ctx.rotate(-rotationAngle + Math.PI / 4);

                    const stick2Gradient = ctx.createLinearGradient(-2, -14, 2, 14);
                    stick2Gradient.addColorStop(0, '#6A1B9A');
                    stick2Gradient.addColorStop(0.5, '#9C27B0');
                    stick2Gradient.addColorStop(1, '#6A1B9A');
                    ctx.fillStyle = stick2Gradient;
                    ctx.shadowColor = '#BA68C8';
                    ctx.shadowBlur = 12;
                    ctx.fillRect(-2.5, -14, 5, 28);

                    ctx.fillStyle = '#FFD700';
                    ctx.shadowBlur = 6;
                    ctx.fillRect(-2.5, -10, 5, 3);
                    ctx.fillRect(-2.5, 7, 5, 3);
                    ctx.shadowBlur = 0;
                    ctx.restore();

                    // Central sparkle burst
                    const sparkleSize = 3 + Math.sin(time * 0.25) * 1.5;
                    const sparkleGradient = ctx.createRadialGradient(0, 0, 0, 0, 0, sparkleSize + 3);
                    sparkleGradient.addColorStop(0, '#FFD700');
                    sparkleGradient.addColorStop(0.5, '#FFC107');
                    sparkleGradient.addColorStop(1, 'rgba(255, 193, 7, 0)');
                    ctx.fillStyle = sparkleGradient;
                    ctx.shadowBlur = 20;
                    ctx.shadowColor = '#FFD700';
                    ctx.beginPath();
                    ctx.arc(0, 0, sparkleSize + 3, 0, Math.PI * 2);
                    ctx.fill();

                    ctx.shadowBlur = 0;
                    break;
                }

                case 'christmas': {
                    // Luxury Christmas Tree & Gift
                    ctx.translate(centerX, centerY);

                    // Tree section
                    ctx.save();
                    ctx.translate(-12, 0);

                    const treeScale = 1 + Math.sin(time * 0.06) * 0.08;
                    ctx.scale(treeScale, treeScale);

                    // Tree shadow/glow
                    const treeGlow = ctx.createRadialGradient(0, 5, 0, 0, 5, 18);
                    treeGlow.addColorStop(0, 'rgba(76, 175, 80, 0.4)');
                    treeGlow.addColorStop(1, 'rgba(76, 175, 80, 0)');
                    ctx.fillStyle = treeGlow;
                    ctx.beginPath();
                    ctx.arc(0, 5, 18, 0, Math.PI * 2);
                    ctx.fill();

                    // Tree layers with gradient
                    const treeGradient = ctx.createLinearGradient(0, -10, 0, 12);
                    treeGradient.addColorStop(0, '#66BB6A');
                    treeGradient.addColorStop(0.5, '#4CAF50');
                    treeGradient.addColorStop(1, '#388E3C');
                    ctx.fillStyle = treeGradient;
                    ctx.shadowColor = '#81C784';
                    ctx.shadowBlur = 15;

                    // Bottom layer
                    ctx.beginPath();
                    ctx.moveTo(0, 6);
                    ctx.lineTo(8, 12);
                    ctx.lineTo(-8, 12);
                    ctx.closePath();
                    ctx.fill();

                    // Middle layer
                    ctx.beginPath();
                    ctx.moveTo(0, -2);
                    ctx.lineTo(7, 6);
                    ctx.lineTo(-7, 6);
                    ctx.closePath();
                    ctx.fill();

                    // Top layer
                    ctx.beginPath();
                    ctx.moveTo(0, -10);
                    ctx.lineTo(6, -2);
                    ctx.lineTo(-6, -2);
                    ctx.closePath();
                    ctx.fill();

                    ctx.shadowBlur = 0;

                    // Wooden trunk
                    ctx.fillStyle = '#6D4C41';
                    ctx.fillRect(-2, 12, 4, 4);

                    // Animated ornaments
                    const ornaments = [
                        { x: -5, y: 9, color: '#F44336', size: 1.5 },
                        { x: 5, y: 9, color: '#2196F3', size: 1.5 },
                        { x: -3, y: 4, color: '#FFC107', size: 1.3 },
                        { x: 3, y: 4, color: '#E91E63', size: 1.3 },
                        { x: 0, y: 0, color: '#9C27B0', size: 1.2 }
                    ];

                    ornaments.forEach((ornament, index) => {
                        const blink = Math.sin(time * 0.15 + index * 0.8) * 0.5 + 0.5;
                        ctx.globalAlpha = 0.6 + blink * 0.4;

                        const ornamentGradient = ctx.createRadialGradient(
                            ornament.x, ornament.y, 0,
                            ornament.x, ornament.y, ornament.size * 2
                        );
                        ornamentGradient.addColorStop(0, ornament.color);
                        ornamentGradient.addColorStop(1, `${ornament.color}80`);

                        ctx.fillStyle = ornamentGradient;
                        ctx.shadowColor = ornament.color;
                        ctx.shadowBlur = 10;
                        ctx.beginPath();
                        ctx.arc(ornament.x, ornament.y, ornament.size, 0, Math.PI * 2);
                        ctx.fill();
                    });

                    ctx.globalAlpha = 1;
                    ctx.shadowBlur = 0;

                    // Rotating star
                    ctx.save();
                    ctx.translate(0, -10);
                    ctx.rotate(time * 0.08);

                    const starGradient = ctx.createRadialGradient(0, 0, 0, 0, 0, 5);
                    starGradient.addColorStop(0, '#FFD700');
                    starGradient.addColorStop(1, '#FFA000');
                    ctx.fillStyle = starGradient;
                    ctx.shadowColor = '#FFD700';
                    ctx.shadowBlur = 18;

                    ctx.beginPath();
                    for (let i = 0; i < 5; i += 1) {
                        const angle = (i * 4 * Math.PI) / 5 - Math.PI / 2;
                        const radius = i % 2 === 0 ? 4 : 2;
                        const x = Math.cos(angle) * radius;
                        const y = Math.sin(angle) * radius;
                        if (i === 0) ctx.moveTo(x, y);
                        else ctx.lineTo(x, y);
                    }
                    ctx.closePath();
                    ctx.fill();
                    ctx.shadowBlur = 0;
                    ctx.restore();
                    ctx.restore();

                    // Premium gift box
                    ctx.save();
                    ctx.translate(12, 4);
                    const giftBounce = Math.sin(time * 0.12) * 1.5;

                    // Gift shadow
                    ctx.fillStyle = 'rgba(0, 0, 0, 0.15)';
                    ctx.beginPath();
                    ctx.ellipse(0, 10, 6, 2, 0, 0, Math.PI * 2);
                    ctx.fill();

                    // Box gradient
                    const boxGradient = ctx.createLinearGradient(-6, giftBounce, 6, giftBounce + 10);
                    boxGradient.addColorStop(0, '#EC407A');
                    boxGradient.addColorStop(1, '#C2185B');
                    ctx.fillStyle = boxGradient;
                    ctx.shadowColor = '#E91E63';
                    ctx.shadowBlur = 10;
                    ctx.fillRect(-6, giftBounce, 12, 10);
                    ctx.shadowBlur = 0;

                    // Gold ribbon
                    ctx.fillStyle = '#FFD700';
                    ctx.shadowColor = '#FFC107';
                    ctx.shadowBlur = 8;
                    ctx.fillRect(-6, 4 + giftBounce, 12, 2.5);
                    ctx.fillRect(-1.5, giftBounce, 3, 10);

                    // Ribbon bows
                    ctx.beginPath();
                    ctx.arc(-3, giftBounce, 2.5, 0, Math.PI * 2);
                    ctx.fill();
                    ctx.beginPath();
                    ctx.arc(3, giftBounce, 2.5, 0, Math.PI * 2);
                    ctx.fill();
                    ctx.shadowBlur = 0;

                    ctx.restore();
                    break;
                }


                case 'lohri': {
                    // Realistic Bonfire with Embers
                    ctx.translate(centerX, centerY);

                    // Wood stack with depth
                    const woodColors = ['#5D4037', '#4E342E', '#3E2723'];

                    // Bottom logs
                    ctx.fillStyle = woodColors[0];
                    ctx.fillRect(-14, 10, 28, 3);
                    ctx.fillStyle = woodColors[1];
                    ctx.fillRect(-12, 7, 24, 3);
                    ctx.fillStyle = woodColors[2];
                    ctx.fillRect(-10, 4, 20, 3);

                    // Multi-layered flames
                    for (let i = 0; i < 6; i += 1) {
                        const flameX = -12 + (i * 4.8);
                        const baseHeight = 16 + (i % 2) * 4;
                        const flameHeight = baseHeight + Math.sin(time * 0.18 + i * 0.8) * 6;
                        const flameWidth = 5 + Math.sin(time * 0.12 + i * 0.6) * 2;
                        const flameY = -flameHeight / 2;

                        // Flame glow
                        const glowGradient = ctx.createRadialGradient(
                            flameX, flameY, 0,
                            flameX, flameY, flameHeight * 0.8
                        );
                        glowGradient.addColorStop(0, 'rgba(255, 200, 0, 0.4)');
                        glowGradient.addColorStop(1, 'rgba(255, 100, 0, 0)');
                        ctx.fillStyle = glowGradient;
                        ctx.beginPath();
                        ctx.ellipse(flameX, flameY, flameWidth * 1.5, flameHeight, 0, 0, Math.PI * 2);
                        ctx.fill();

                        // Bottom layer (red-orange)
                        const bottomGradient = ctx.createLinearGradient(
                            flameX, flameY + 5,
                            flameX, flameY - 5
                        );
                        bottomGradient.addColorStop(0, '#FF3D00');
                        bottomGradient.addColorStop(1, '#FF6F00');
                        ctx.fillStyle = bottomGradient;
                        ctx.shadowBlur = 18;
                        ctx.shadowColor = '#FF5722';
                        ctx.beginPath();
                        ctx.ellipse(flameX, flameY + 5, flameWidth * 0.9, flameHeight * 0.45, 0, 0, Math.PI * 2);
                        ctx.fill();

                        // Middle layer (orange)
                        const middleGradient = ctx.createLinearGradient(
                            flameX, flameY,
                            flameX, flameY - 8
                        );
                        middleGradient.addColorStop(0, '#FF9800');
                        middleGradient.addColorStop(1, '#FFB300');
                        ctx.fillStyle = middleGradient;
                        ctx.shadowBlur = 15;
                        ctx.shadowColor = '#FF9800';
                        ctx.beginPath();
                        ctx.ellipse(flameX, flameY, flameWidth * 0.7, flameHeight * 0.55, 0, 0, Math.PI * 2);
                        ctx.fill();

                        // Top layer (yellow-white)
                        const topGradient = ctx.createLinearGradient(
                            flameX, flameY - 5,
                            flameX, flameY - 12
                        );
                        topGradient.addColorStop(0, '#FFC107');
                        topGradient.addColorStop(1, '#FFF9C4');
                        ctx.fillStyle = topGradient;
                        ctx.shadowBlur = 12;
                        ctx.shadowColor = '#FFC107';
                        ctx.beginPath();
                        ctx.ellipse(flameX, flameY - 5, flameWidth * 0.5, flameHeight * 0.35, 0, 0, Math.PI * 2);
                        ctx.fill();
                    }

                    ctx.shadowBlur = 0;

                    // Flying embers with trails
                    for (let e = 0; e < 12; e += 1) {
                        const emberTime = (time * 2.2 + e * 10) % 120;
                        const emberProgress = emberTime / 120;

                        if (emberProgress < 0.85) {
                            const emberX = -18 + (e % 5) * 9 + Math.sin(emberTime * 0.08) * 6;
                            const emberY = 8 - (emberProgress * 40);
                            const emberSize = 2 - (emberProgress * 1.5);
                            const emberAlpha = Math.max(0, 1 - (emberProgress * 1.18));

                            // Ember trail
                            ctx.globalAlpha = emberAlpha * 0.3;
                            ctx.strokeStyle = e % 2 === 0 ? '#FF5722' : '#FFC107';
                            ctx.lineWidth = 1;
                            ctx.beginPath();
                            ctx.moveTo(emberX, emberY + 3);
                            ctx.lineTo(emberX, emberY);
                            ctx.stroke();

                            // Ember particle
                            ctx.globalAlpha = emberAlpha;
                            const emberGradient = ctx.createRadialGradient(
                                emberX, emberY, 0,
                                emberX, emberY, emberSize + 1
                            );
                            emberGradient.addColorStop(0, e % 2 === 0 ? '#FFC107' : '#FF5722');
                            emberGradient.addColorStop(1, 'rgba(255, 87, 34, 0)');
                            ctx.fillStyle = emberGradient;
                            ctx.shadowBlur = 10;
                            ctx.shadowColor = e % 2 === 0 ? '#FFC107' : '#FF5722';
                            ctx.beginPath();
                            ctx.arc(emberX, emberY, emberSize, 0, Math.PI * 2);
                            ctx.fill();
                        }
                    }

                    ctx.globalAlpha = 1;
                    ctx.shadowBlur = 0;
                    break;
                }

                case 'eid': {
                    // Elegant Crescent Moon & Stars
                    ctx.translate(centerX, centerY);

                    // Ambient glow
                    const ambientGlow = ctx.createRadialGradient(0, 0, 0, 0, 0, 25);
                    ambientGlow.addColorStop(0, 'rgba(76, 175, 80, 0.2)');
                    ambientGlow.addColorStop(1, 'rgba(76, 175, 80, 0)');
                    ctx.fillStyle = ambientGlow;
                    ctx.beginPath();
                    ctx.arc(0, 0, 25, 0, Math.PI * 2);
                    ctx.fill();

                    // Orbiting stars with varying sizes
                    const starSizes = [2.5, 2, 1.8, 2.2, 1.5, 2.8];
                    for (let s = 0; s < 6; s += 1) {
                        const angle = (time * 0.06) + (s * Math.PI * 2 / 6);
                        const orbitRadius = 14 + Math.sin(s) * 2;
                        const starX = Math.cos(angle) * orbitRadius;
                        const starY = Math.sin(angle) * orbitRadius;
                        const baseSize = starSizes[s];
                        const pulseSize = baseSize + Math.sin(time * 0.15 + s) * 0.5;

                        // Star glow
                        const starGlow = ctx.createRadialGradient(
                            starX, starY, 0,
                            starX, starY, pulseSize + 2
                        );
                        starGlow.addColorStop(0, '#FFD700');
                        starGlow.addColorStop(1, 'rgba(255, 215, 0, 0)');
                        ctx.fillStyle = starGlow;
                        ctx.beginPath();
                        ctx.arc(starX, starY, pulseSize + 2, 0, Math.PI * 2);
                        ctx.fill();

                        // Star core
                        ctx.fillStyle = '#FFD700';
                        ctx.shadowColor = '#FFD700';
                        ctx.shadowBlur = 10;
                        ctx.beginPath();
                        ctx.arc(starX, starY, pulseSize, 0, Math.PI * 2);
                        ctx.fill();
                    }

                    ctx.shadowBlur = 0;

                    // Crescent moon with gradient
                    ctx.save();
                    const moonRotation = Math.sin(time * 0.06) * 0.25;
                    ctx.rotate(moonRotation);

                    // Moon glow
                    const moonGlow = ctx.createRadialGradient(0, 0, 0, 0, 0, 14);
                    moonGlow.addColorStop(0, 'rgba(76, 175, 80, 0.6)');
                    moonGlow.addColorStop(1, 'rgba(76, 175, 80, 0)');
                    ctx.fillStyle = moonGlow;
                    ctx.beginPath();
                    ctx.arc(0, 0, 14, 0, Math.PI * 2);
                    ctx.fill();

                    // Moon gradient
                    const moonGradient = ctx.createRadialGradient(-2, -2, 0, 0, 0, 10);
                    moonGradient.addColorStop(0, '#81C784');
                    moonGradient.addColorStop(1, '#4CAF50');
                    ctx.fillStyle = moonGradient;
                    ctx.shadowColor = '#4CAF50';
                    ctx.shadowBlur = 16;
                    ctx.beginPath();
                    ctx.arc(0, 0, 9, 0, Math.PI * 2);
                    ctx.fill();
                    ctx.shadowBlur = 0;

                    // Crescent cutout
                    ctx.globalCompositeOperation = 'destination-out';
                    ctx.fillStyle = '#000000';
                    ctx.beginPath();
                    ctx.arc(3.5, -2.5, 8, 0, Math.PI * 2);
                    ctx.fill();
                    ctx.globalCompositeOperation = 'source-over';

                    ctx.restore();
                    break;
                }

                case 'ramnavmi': {
                    // Majestic Bow & Arrow
                    ctx.translate(centerX, centerY);
                    const bowTilt = Math.sin(time * 0.06) * 0.12;
                    ctx.rotate(bowTilt);

                    // Bow glow
                    const bowGlow = ctx.createRadialGradient(0, 0, 0, 0, 0, 20);
                    bowGlow.addColorStop(0, 'rgba(141, 110, 99, 0.3)');
                    bowGlow.addColorStop(1, 'rgba(141, 110, 99, 0)');
                    ctx.fillStyle = bowGlow;
                    ctx.beginPath();
                    ctx.arc(0, 0, 20, 0, Math.PI * 2);
                    ctx.fill();

                    // Bow with gradient
                    const bowGradient = ctx.createLinearGradient(0, -14, 0, 14);
                    bowGradient.addColorStop(0, '#A1887F');
                    bowGradient.addColorStop(0.5, '#8D6E63');
                    bowGradient.addColorStop(1, '#795548');
                    ctx.strokeStyle = bowGradient;
                    ctx.lineWidth = 2.5;
                    ctx.shadowColor = '#8D6E63';
                    ctx.shadowBlur = 10;
                    ctx.lineCap = 'round';
                    ctx.beginPath();
                    ctx.arc(0, 0, 13, -Math.PI * 0.75, Math.PI * 0.75, false);
                    ctx.stroke();
                    ctx.shadowBlur = 0;

                    // Bow string with shimmer
                    const bowTop = {
                        x: Math.cos(-Math.PI * 0.75) * 13,
                        y: Math.sin(-Math.PI * 0.75) * 13
                    };
                    const bowBottom = {
                        x: Math.cos(Math.PI * 0.75) * 13,
                        y: Math.sin(Math.PI * 0.75) * 13
                    };

                    ctx.strokeStyle = '#FFF9C4';
                    ctx.lineWidth = 1.2;
                    ctx.shadowColor = '#FFEB3B';
                    ctx.shadowBlur = 8;
                    ctx.beginPath();
                    ctx.moveTo(bowTop.x, bowTop.y);
                    ctx.lineTo(bowBottom.x, bowBottom.y);
                    ctx.stroke();
                    ctx.shadowBlur = 0;

                    // Animated arrow
                    const arrowPull = Math.sin(time * 0.15) * 4;
                    const arrowX = -18 + arrowPull;

                    // Arrow glow
                    const arrowGlow = ctx.createRadialGradient(0, 0, 0, 0, 0, 10);
                    arrowGlow.addColorStop(0, 'rgba(255, 111, 0, 0.4)');
                    arrowGlow.addColorStop(1, 'rgba(255, 111, 0, 0)');
                    ctx.fillStyle = arrowGlow;
                    ctx.beginPath();
                    ctx.ellipse(0, 0, 15, 8, 0, 0, Math.PI * 2);
                    ctx.fill();

                    // Arrow shaft gradient
                    const arrowGradient = ctx.createLinearGradient(arrowX, 0, 6, 0);
                    arrowGradient.addColorStop(0, '#FF8F00');
                    arrowGradient.addColorStop(1, '#FF6F00');
                    ctx.strokeStyle = arrowGradient;
                    ctx.lineWidth = 2.5;
                    ctx.shadowColor = '#FF6F00';
                    ctx.shadowBlur = 12;
                    ctx.lineCap = 'round';
                    ctx.beginPath();
                    ctx.moveTo(arrowX, 0);
                    ctx.lineTo(6, 0);
                    ctx.stroke();

                    // Arrow head
                    ctx.fillStyle = '#FF6F00';
                    ctx.shadowBlur = 10;
                    ctx.beginPath();
                    ctx.moveTo(6, 0);
                    ctx.lineTo(2, -4);
                    ctx.lineTo(4, 0);
                    ctx.lineTo(2, 4);
                    ctx.closePath();
                    ctx.fill();

                    // Arrow feathers
                    ctx.strokeStyle = '#FFD700';
                    ctx.lineWidth = 1.5;
                    ctx.shadowBlur = 6;
                    for (let i = -1; i <= 1; i += 1) {
                        ctx.beginPath();
                        ctx.moveTo(arrowX + 2, i * 2);
                        ctx.lineTo(arrowX - 2, i * 3);
                        ctx.stroke();
                    }

                    ctx.shadowBlur = 0;
                    break;
                }

                case 'rakshabandhan': {
                    // Ornate Rakhi with Threading
                    ctx.translate(centerX, centerY);
                    const rakhiRotation = time * 0.04;
                    ctx.rotate(rakhiRotation);

                    // Rakhi glow
                    const rakhiGlow = ctx.createRadialGradient(0, 0, 0, 0, 0, 18);
                    rakhiGlow.addColorStop(0, 'rgba(233, 30, 99, 0.4)');
                    rakhiGlow.addColorStop(1, 'rgba(233, 30, 99, 0)');
                    ctx.fillStyle = rakhiGlow;
                    ctx.beginPath();
                    ctx.arc(0, 0, 18, 0, Math.PI * 2);
                    ctx.fill();

                    // Outer decorative ring
                    ctx.strokeStyle = '#F48FB1';
                    ctx.lineWidth = 2;
                    ctx.shadowColor = '#FF4081';
                    ctx.shadowBlur = 8;
                    ctx.beginPath();
                    ctx.arc(0, 0, 9, 0, Math.PI * 2);
                    ctx.stroke();

                    // Central ornament gradient
                    const centerGradient = ctx.createRadialGradient(0, 0, 0, 0, 0, 7);
                    centerGradient.addColorStop(0, '#EC407A');
                    centerGradient.addColorStop(0.6, '#E91E63');
                    centerGradient.addColorStop(1, '#C2185B');
                    ctx.fillStyle = centerGradient;
                    ctx.shadowBlur = 14;
                    ctx.shadowColor = '#FF4081';
                    ctx.beginPath();
                    ctx.arc(0, 0, 7, 0, Math.PI * 2);
                    ctx.fill();

                    // Inner gold circle
                    const goldGradient = ctx.createRadialGradient(-1, -1, 0, 0, 0, 4);
                    goldGradient.addColorStop(0, '#FFD700');
                    goldGradient.addColorStop(1, '#FFA000');
                    ctx.fillStyle = goldGradient;
                    ctx.shadowBlur = 10;
                    ctx.shadowColor = '#FFD700';
                    ctx.beginPath();
                    ctx.arc(0, 0, 4, 0, Math.PI * 2);
                    ctx.fill();

                    ctx.shadowBlur = 0;

                    // Decorative petals with varying sizes
                    const petalPattern = [3, 2.5, 3, 2.5, 3, 2.5, 3, 2.5];
                    for (let p = 0; p < 8; p += 1) {
                        const angle = (Math.PI * 2 * p) / 8;
                        const petalRadius = 11 + Math.sin(time * 0.12 + p) * 1;
                        const petalX = Math.cos(angle) * petalRadius;
                        const petalY = Math.sin(angle) * petalRadius;
                        const petalSize = petalPattern[p];

                        const petalGradient = ctx.createRadialGradient(
                            petalX, petalY, 0,
                            petalX, petalY, petalSize
                        );

                        if (p % 2 === 0) {
                            petalGradient.addColorStop(0, '#FF4081');
                            petalGradient.addColorStop(1, '#F06292');
                        } else {
                            petalGradient.addColorStop(0, '#FFD700');
                            petalGradient.addColorStop(1, '#FFC107');
                        }

                        ctx.fillStyle = petalGradient;
                        ctx.shadowColor = p % 2 === 0 ? '#FF4081' : '#FFD700';
                        ctx.shadowBlur = 8;
                        ctx.beginPath();
                        ctx.arc(petalX, petalY, petalSize, 0, Math.PI * 2);
                        ctx.fill();
                    }

                    ctx.shadowBlur = 0;
                    ctx.restore();

                    // Thread waves (outside rotation)
                    ctx.save();
                    ctx.translate(centerX, centerY);

                    for (let side = -1; side <= 1; side += 2) {
                        // Thread gradient
                        const threadGradient = ctx.createLinearGradient(0, 0, side * 20, 0);
                        threadGradient.addColorStop(0, '#FF4081');
                        threadGradient.addColorStop(1, '#F48FB1');
                        ctx.strokeStyle = threadGradient;
                        ctx.lineWidth = 2.5;
                        ctx.shadowColor = '#FF4081';
                        ctx.shadowBlur = 6;
                        ctx.lineCap = 'round';

                        ctx.beginPath();
                        for (let x = 0; x < 20; x += 1) {
                            const y = Math.sin((x + time * 1.5) * 0.4) * 2.5;
                            if (x === 0) ctx.moveTo(side * x, y);
                            else ctx.lineTo(side * x, y);
                        }
                        ctx.stroke();
                    }

                    ctx.shadowBlur = 0;
                    ctx.restore();
                    break;
                }

                case 'uttarayan': {
                    // Dynamic Flying Kite
                    const kiteY = centerY + Math.sin(time * 0.12) * 4;
                    const kiteAngle = Math.sin(time * 0.08) * 0.35;

                    ctx.translate(centerX, kiteY);
                    ctx.rotate(kiteAngle);

                    // Kite shadow
                    ctx.fillStyle = 'rgba(0, 0, 0, 0.15)';
                    ctx.beginPath();
                    ctx.ellipse(2, 15, 8, 3, 0.3, 0, Math.PI * 2);
                    ctx.fill();

                    // Kite glow
                    const kiteGlow = ctx.createRadialGradient(0, 0, 0, 0, 0, 18);
                    kiteGlow.addColorStop(0, 'rgba(233, 30, 99, 0.4)');
                    kiteGlow.addColorStop(1, 'rgba(233, 30, 99, 0)');
                    ctx.fillStyle = kiteGlow;
                    ctx.beginPath();
                    ctx.arc(0, 0, 18, 0, Math.PI * 2);
                    ctx.fill();

                    // Kite body gradient
                    const kiteGradient = ctx.createLinearGradient(-10, -14, 10, 14);
                    kiteGradient.addColorStop(0, '#EC407A');
                    kiteGradient.addColorStop(0.5, '#E91E63');
                    kiteGradient.addColorStop(1, '#C2185B');
                    ctx.fillStyle = kiteGradient;
                    ctx.shadowColor = '#FF4081';
                    ctx.shadowBlur = 12;

                    ctx.beginPath();
                    ctx.moveTo(0, -14);
                    ctx.lineTo(10, 0);
                    ctx.lineTo(0, 14);
                    ctx.lineTo(-10, 0);
                    ctx.closePath();
                    ctx.fill();

                    ctx.shadowBlur = 0;

                    // Kite frame
                    ctx.strokeStyle = '#FFD700';
                    ctx.lineWidth = 1.5;
                    ctx.shadowColor = '#FFC107';
                    ctx.shadowBlur = 6;
                    ctx.beginPath();
                    ctx.moveTo(0, -14);
                    ctx.lineTo(0, 14);
                    ctx.moveTo(-10, 0);
                    ctx.lineTo(10, 0);
                    ctx.stroke();
                    ctx.shadowBlur = 0;

                    // Decorative patterns
                    ctx.strokeStyle = '#FFFFFF';
                    ctx.lineWidth = 0.8;
                    ctx.globalAlpha = 0.6;

                    // Top triangle pattern
                    ctx.beginPath();
                    ctx.moveTo(-5, -7);
                    ctx.lineTo(5, -7);
                    ctx.lineTo(0, -2);
                    ctx.closePath();
                    ctx.stroke();

                    // Bottom triangle pattern
                    ctx.beginPath();
                    ctx.moveTo(-5, 7);
                    ctx.lineTo(5, 7);
                    ctx.lineTo(0, 2);
                    ctx.closePath();
                    ctx.stroke();

                    ctx.globalAlpha = 1;

                    // Ribbon tails with physics
                    ctx.strokeStyle = '#FFC107';
                    ctx.lineWidth = 2.5;
                    ctx.shadowColor = '#FFC107';
                    ctx.shadowBlur = 8;
                    ctx.lineCap = 'round';

                    for (let t = 0; t < 3; t += 1) {
                        const ribbonOffset = t * 3 - 3;
                        ctx.beginPath();

                        for (let i = 0; i < 10; i += 1) {
                            const x = ribbonOffset + i * 1.5;
                            const waveAmplitude = 4 + (i * 0.3);
                            const y = 14 + i * 2.5 + Math.sin((time + t * 12) * 0.15 + i * 0.6) * waveAmplitude;

                            if (i === 0) ctx.moveTo(ribbonOffset, 14);
                            else ctx.lineTo(x, y);
                        }

                        ctx.stroke();
                    }

                    ctx.shadowBlur = 0;
                    break;
                }

                case 'onam': {
                    // Intricate Pookalam (Flower Rangoli)
                    ctx.translate(centerX, centerY);
                    const pookalamRotation = time * 0.03;
                    ctx.rotate(pookalamRotation);

                    // Rangoli glow
                    const rangoliGlow = ctx.createRadialGradient(0, 0, 0, 0, 0, 20);
                    rangoliGlow.addColorStop(0, 'rgba(255, 152, 0, 0.3)');
                    rangoliGlow.addColorStop(1, 'rgba(255, 152, 0, 0)');
                    ctx.fillStyle = rangoliGlow;
                    ctx.beginPath();
                    ctx.arc(0, 0, 20, 0, Math.PI * 2);
                    ctx.fill();

                    // Multiple flower layers with gradients
                    const layers = [
                        { radius: 13, color: '#FF9800', secondaryColor: '#FFB74D', count: 8, size: 2.8 },
                        { radius: 9, color: '#FFEB3B', secondaryColor: '#FFF176', count: 6, size: 2.5 },
                        { radius: 5, color: '#E91E63', secondaryColor: '#F06292', count: 4, size: 2.2 }
                    ];

                    layers.forEach((layer, layerIndex) => {
                        for (let f = 0; f < layer.count; f += 1) {
                            const angle = (Math.PI * 2 * f) / layer.count + (time * 0.03 * (layerIndex + 1));
                            const flowerX = Math.cos(angle) * layer.radius;
                            const flowerY = Math.sin(angle) * layer.radius;
                            const pulsing = layer.size + Math.sin(time * 0.15 + f) * 0.5;

                            // Flower glow
                            const flowerGlow = ctx.createRadialGradient(
                                flowerX, flowerY, 0,
                                flowerX, flowerY, pulsing + 2
                            );
                            flowerGlow.addColorStop(0, layer.secondaryColor);
                            flowerGlow.addColorStop(1, `${layer.color}00`);
                            ctx.fillStyle = flowerGlow;
                            ctx.beginPath();
                            ctx.arc(flowerX, flowerY, pulsing + 2, 0, Math.PI * 2);
                            ctx.fill();

                            // Flower petal gradient
                            const petalGradient = ctx.createRadialGradient(
                                flowerX, flowerY, 0,
                                flowerX, flowerY, pulsing
                            );
                            petalGradient.addColorStop(0, layer.secondaryColor);
                            petalGradient.addColorStop(1, layer.color);
                            ctx.fillStyle = petalGradient;
                            ctx.shadowColor = layer.color;
                            ctx.shadowBlur = 10;
                            ctx.beginPath();
                            ctx.arc(flowerX, flowerY, pulsing, 0, Math.PI * 2);
                            ctx.fill();
                        }
                    });

                    ctx.shadowBlur = 0;

                    // Center flower with detail
                    const centerGradient = ctx.createRadialGradient(-1, -1, 0, 0, 0, 4);
                    centerGradient.addColorStop(0, '#81C784');
                    centerGradient.addColorStop(0.5, '#4CAF50');
                    centerGradient.addColorStop(1, '#388E3C');
                    ctx.fillStyle = centerGradient;
                    ctx.shadowColor = '#4CAF50';
                    ctx.shadowBlur = 15;
                    ctx.beginPath();
                    ctx.arc(0, 0, 4, 0, Math.PI * 2);
                    ctx.fill();

                    // Center highlight
                    ctx.fillStyle = '#A5D6A7';
                    ctx.shadowBlur = 8;
                    ctx.beginPath();
                    ctx.arc(-0.5, -0.5, 2, 0, Math.PI * 2);
                    ctx.fill();

                    ctx.shadowBlur = 0;
                    break;
                }

                case 'ganeshchaturthi': {
                    // Premium Glowing Modak
                    ctx.translate(centerX, centerY + Math.sin(time * 0.12) * 2.5);

                    // Modak glow
                    const modakGlow = ctx.createRadialGradient(0, 0, 0, 0, 0, 18);
                    modakGlow.addColorStop(0, 'rgba(255, 152, 0, 0.5)');
                    modakGlow.addColorStop(0.7, 'rgba(255, 193, 7, 0.2)');
                    modakGlow.addColorStop(1, 'rgba(255, 215, 0, 0)');
                    ctx.fillStyle = modakGlow;
                    ctx.beginPath();
                    ctx.arc(0, 0, 18, 0, Math.PI * 2);
                    ctx.fill();

                    // Modak gradient
                    const modakGradient = ctx.createLinearGradient(-8, -12, 8, 12);
                    modakGradient.addColorStop(0, '#FFB300');
                    modakGradient.addColorStop(0.5, '#FF9800');
                    modakGradient.addColorStop(1, '#F57C00');
                    ctx.fillStyle = modakGradient;
                    ctx.shadowColor = '#FFD700';
                    ctx.shadowBlur = 15;

                    ctx.beginPath();
                    ctx.moveTo(0, -12);
                    ctx.quadraticCurveTo(10, -5, 8, 6);
                    ctx.quadraticCurveTo(4, 12, 0, 12);
                    ctx.quadraticCurveTo(-4, 12, -8, 6);
                    ctx.quadraticCurveTo(-10, -5, 0, -12);
                    ctx.closePath();
                    ctx.fill();

                    // Modak pleats
                    ctx.strokeStyle = '#F57C00';
                    ctx.lineWidth = 1.5;
                    ctx.shadowBlur = 5;
                    for (let i = -1; i <= 1; i += 1) {
                        ctx.beginPath();
                        ctx.moveTo(i * 4, -8);
                        ctx.lineTo(i * 3, 8);
                        ctx.stroke();
                    }

                    ctx.shadowBlur = 0;

                    // Sparkles around modak
                    for (let s = 0; s < 8; s += 1) {
                        const sparkleAngle = (time * 0.08) + (s * Math.PI / 4);
                        const sparkleRadius = 16 + Math.sin(time * 0.15 + s) * 2;
                        const sparkleX = Math.cos(sparkleAngle) * sparkleRadius;
                        const sparkleY = Math.sin(sparkleAngle) * sparkleRadius;
                        const sparkleSize = 1.5 + Math.sin(time * 0.2 + s) * 0.7;

                        ctx.fillStyle = '#FFD700';
                        ctx.shadowColor = '#FFD700';
                        ctx.shadowBlur = 8;
                        ctx.beginPath();
                        ctx.arc(sparkleX, sparkleY, sparkleSize, 0, Math.PI * 2);
                        ctx.fill();
                    }

                    ctx.shadowBlur = 0;
                    break;
                }

                case 'republic':
                case 'independence': {
                    // Enhanced Indian Flag with 3D Wave
                    const waveSpeed = time * 0.12;
                    const colors = ['#FF9933', '#FFFFFF', '#138808'];
                    const stripHeight = canvas.height / 3;

                    ctx.save();

                    // Flag pole
                    ctx.fillStyle = '#8D6E63';
                    ctx.fillRect(2, 0, 3, canvas.height);

                    // Draw flag with realistic wave
                    for (let i = 0; i < 3; i += 1) {
                        const gradient = ctx.createLinearGradient(0, i * stripHeight, 0, (i + 1) * stripHeight);

                        if (i === 0) {
                            gradient.addColorStop(0, '#FF9933');
                            gradient.addColorStop(1, '#E67E22');
                        } else if (i === 1) {
                            gradient.addColorStop(0, '#FFFFFF');
                            gradient.addColorStop(1, '#ECF0F1');
                        } else {
                            gradient.addColorStop(0, '#138808');
                            gradient.addColorStop(1, '#0E6B07');
                        }

                        ctx.fillStyle = gradient;
                        ctx.beginPath();

                        for (let x = 5; x <= canvas.width; x += 1) {
                            const waveAmplitude = 4 + Math.sin(x * 0.08) * 1;
                            const yOffset = Math.sin(x * 0.25 + waveSpeed) * waveAmplitude;
                            const y = (i * stripHeight) + yOffset;
                            if (x === 5) ctx.moveTo(x, y);
                            else ctx.lineTo(x, y);
                        }

                        for (let x = canvas.width; x >= 5; x -= 1) {
                            const waveAmplitude = 4 + Math.sin(x * 0.08) * 1;
                            const yOffset = Math.sin(x * 0.25 + waveSpeed) * waveAmplitude;
                            const y = ((i + 1) * stripHeight) + yOffset;
                            ctx.lineTo(x, y);
                        }

                        ctx.closePath();
                        ctx.fill();

                        // Add subtle shadow for depth
                        ctx.strokeStyle = 'rgba(0, 0, 0, 0.1)';
                        ctx.lineWidth = 0.5;
                        ctx.stroke();
                    }

                    // Ashoka Chakra with detail
                    const chakraX = canvas.width / 2 + 8;
                    const chakraY = canvas.height / 2;
                    const chakraRadius = stripHeight * 0.35;

                    ctx.translate(chakraX, chakraY);
                    ctx.rotate(time * 0.025);

                    // Chakra glow
                    const chakraGlow = ctx.createRadialGradient(0, 0, 0, 0, 0, chakraRadius + 3);
                    chakraGlow.addColorStop(0, 'rgba(0, 0, 128, 0.3)');
                    chakraGlow.addColorStop(1, 'rgba(0, 0, 128, 0)');
                    ctx.fillStyle = chakraGlow;
                    ctx.beginPath();
                    ctx.arc(0, 0, chakraRadius + 3, 0, Math.PI * 2);
                    ctx.fill();

                    // Outer circle
                    ctx.strokeStyle = '#000080';
                    ctx.lineWidth = 1.5;
                    ctx.shadowColor = '#000080';
                    ctx.shadowBlur = 4;
                    ctx.beginPath();
                    ctx.arc(0, 0, chakraRadius, 0, Math.PI * 2);
                    ctx.stroke();

                    // Inner circle
                    ctx.lineWidth = 1;
                    ctx.beginPath();
                    ctx.arc(0, 0, chakraRadius * 0.85, 0, Math.PI * 2);
                    ctx.stroke();
                    ctx.shadowBlur = 0;

                    // 24 spokes
                    for (let j = 0; j < 24; j += 1) {
                        ctx.strokeStyle = '#000080';
                        ctx.lineWidth = 1.2;
                        ctx.beginPath();
                        ctx.moveTo(0, 0);
                        ctx.lineTo(chakraRadius * 0.85, 0);
                        ctx.stroke();
                        ctx.rotate(Math.PI / 12);
                    }

                    // Center dot
                    ctx.fillStyle = '#000080';
                    ctx.beginPath();
                    ctx.arc(0, 0, 2, 0, Math.PI * 2);
                    ctx.fill();

                    ctx.restore();
                    break;
                }

                case 'comingsoon': {
                    // Coming Soon - Running Person with Text
                    ctx.translate(centerX, centerY);

                    // Animated running person
                    const runCycle = (time * 0.15) % (Math.PI * 2);
                    const bobbing = Math.sin(runCycle * 2) * 1.5;

                    // Person head
                    ctx.fillStyle = '#2196F3';
                    ctx.shadowColor = '#2196F3';
                    ctx.shadowBlur = 8;
                    ctx.beginPath();
                    ctx.arc(-8, -8 + bobbing, 4, 0, Math.PI * 2);
                    ctx.fill();

                    // Person body
                    ctx.strokeStyle = '#2196F3';
                    ctx.lineWidth = 2.5;
                    ctx.lineCap = 'round';
                    ctx.shadowBlur = 10;

                    // Torso
                    ctx.beginPath();
                    ctx.moveTo(-8, -4 + bobbing);
                    ctx.lineTo(-8, 4 + bobbing);
                    ctx.stroke();

                    // Arms (animated)
                    const armSwing = Math.sin(runCycle) * 0.6;
                    ctx.beginPath();
                    ctx.moveTo(-8, -2 + bobbing);
                    ctx.lineTo(-12 + Math.cos(armSwing) * 4, 2 + bobbing + Math.sin(armSwing) * 4);
                    ctx.stroke();

                    ctx.beginPath();
                    ctx.moveTo(-8, -2 + bobbing);
                    ctx.lineTo(-4 + Math.cos(armSwing + Math.PI) * 4, 2 + bobbing + Math.sin(armSwing + Math.PI) * 4);
                    ctx.stroke();

                    // Legs (animated running)
                    const legSwing = Math.sin(runCycle) * 0.8;
                    ctx.beginPath();
                    ctx.moveTo(-8, 4 + bobbing);
                    ctx.lineTo(-10 + Math.cos(legSwing) * 3, 10 + bobbing + Math.sin(legSwing) * 3);
                    ctx.stroke();

                    ctx.beginPath();
                    ctx.moveTo(-8, 4 + bobbing);
                    ctx.lineTo(-6 + Math.cos(legSwing + Math.PI) * 3, 10 + bobbing + Math.sin(legSwing + Math.PI) * 3);
                    ctx.stroke();

                    ctx.shadowBlur = 0;

                    // "Coming Soon" text
                    ctx.font = 'bold 7px Arial';
                    const textGradient = ctx.createLinearGradient(5, 0, 20, 0);
                    textGradient.addColorStop(0, '#00E5FF');
                    textGradient.addColorStop(0.5, '#00BCD4');
                    textGradient.addColorStop(1, '#4CAF50');
                    ctx.fillStyle = textGradient;
                    ctx.shadowColor = '#00BCD4';
                    ctx.shadowBlur = 6;
                    ctx.fillText('Coming', 5, -2);
                    ctx.fillText('Soon', 8, 6);

                    ctx.shadowBlur = 0;
                    break;
                }

                default:
                    // Default festive sparkle
                    ctx.translate(centerX, centerY);
                    for (let i = 0; i < 5; i += 1) {
                        const angle = (time * 0.05) + (i * Math.PI * 2 / 5);
                        const radius = 12;
                        const x = Math.cos(angle) * radius;
                        const y = Math.sin(angle) * radius;
                        const size = 2 + Math.sin(time * 0.1 + i) * 0.5;

                        ctx.fillStyle = '#FFD700';
                        ctx.shadowColor = '#FFD700';
                        ctx.shadowBlur = 10;
                        ctx.beginPath();
                        ctx.arc(x, y, size, 0, Math.PI * 2);
                        ctx.fill();
                    }
                    ctx.shadowBlur = 0;
            }

            ctx.restore();
            time += 1;
            animationFrameRef.current = requestAnimationFrame(animate);
        };

        animate();

        return () => {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
        };
    }, [activeTheme]);

    // Tooltip mapping
    const getFestivalTooltip = () => {
        if (!festivalName) return 'Festival';
        return `Happy ${festivalName}!`;
    };

    if (!activeTheme) return null;

    return (
        <Tooltip title={getFestivalTooltip()} arrow>
            <canvas
                ref={canvasRef}
                style={{
                    width: 60,
                    height: 40,
                    cursor: 'pointer',
                    marginRight: 8,
                    display: 'block',
                    imageRendering: 'crisp-edges'
                }}
            />
        </Tooltip>
    );
}

FestivalHeaderWidget.propTypes = {};
